import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faFilter,
  faDownload,
  faSearch,
  faSync,
  faTable,
  faChevronLeft,
  faChevronRight,
  faSpinner,
  faFileCsv,
  faFileCode
} from '@fortawesome/free-solid-svg-icons';
const ReportRenderer = ({ definition, agentId, token, onClose, componentName }) => {
  // If definition is not provided, try to load it from props (when used via AgentComponent)
  const actualComponentName = componentName || definition?.metadata?.name;
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFilters, setShowFilters] = useState(true);

  // Load data on mount and when filters change
  useEffect(() => {
    if (definition && agentId) {
      loadReportData();
    }
  }, [definition, agentId]);

  // Apply filters and search whenever data or filters change
  useEffect(() => {
    if (definition && agentId) {
      applyFiltersAndSearch();
    }
  }, [data, filters, searchTerm, sortConfig, definition, agentId]);

  // Early return if required props are missing
  if (!definition) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-red-500 mb-2">⚠️ No definition provided</p>
          <p className="text-gray-600 dark:text-gray-400">Report definition is required</p>
        </div>
      </div>
    );
  }

  if (!agentId) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-red-500 mb-2">⚠️ No agent ID provided</p>
          <p className="text-gray-600 dark:text-gray-400">Agent ID is required</p>
        </div>
      </div>
    );
  }

  const loadReportData = async () => {
    setLoading(true);
    try {
      const endpoint = definition.config?.data_source?.replace('{agent_id}', agentId) || 
                      `/api/agents/${agentId}/reports/${actualComponentName}`;
      
      const queryParams = new URLSearchParams(filters);
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Only add auth header if token is available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Check if endpoint already has query parameters
      const separator = endpoint.includes('?') ? '&' : '?';
      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${endpoint}${separator}${queryString}` : endpoint;
      
      console.log('🔍 [DEBUG] ReportRenderer URL construction:', {
        endpoint,
        queryString,
        separator,
        finalUrl,
        filters
      });
      
      const response = await fetch(finalUrl, { headers });

      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
      } else {
        console.error('Failed to load report data');
        setData([]);
      }
    } catch (error) {
      console.error('Error loading report data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let result = [...data];

    // Apply search
    if (searchTerm) {
      result = result.filter(item => 
        Object.values(item).some(value => 
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply custom filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        result = result.filter(item => {
          const itemValue = String(item[key]).toLowerCase();
          return itemValue.includes(String(value).toLowerCase());
        });
      }
    });

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredData(result);
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const exportData = (format) => {
    const dataToExport = filteredData.length > 0 ? filteredData : data;
    
    if (format === 'csv') {
      exportToCSV(dataToExport);
    } else if (format === 'json') {
      exportToJSON(dataToExport);
    }
  };

  const exportToCSV = (dataToExport) => {
    if (dataToExport.length === 0) return;
    
    const headers = Object.keys(dataToExport[0]);
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape quotes and wrap in quotes if contains comma
          const escaped = String(value).replace(/"/g, '""');
          return escaped.includes(',') ? `"${escaped}"` : escaped;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${definition.metadata?.name || 'report'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToJSON = (dataToExport) => {
    const jsonContent = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${definition.metadata?.name || 'report'}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Get columns from definition or auto-detect from data
  const columns = definition.config?.columns || 
    (data.length > 0 ? Object.keys(data[0]).map(key => ({
      key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      sortable: true
    })) : []);

  // Helper function to render cell content with wrapping and truncation
  const renderCellContent = (value, column) => {
    const stringValue = String(value || '');
    const maxLines = column.maxLines || 4;
    const isLongContent = stringValue.length > 100;

    if (isLongContent) {
      return (
        <div 
          className="text-sm leading-5 max-w-xs"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            whiteSpace: 'normal'
          }}
          title={stringValue} // Show full text on hover
        >
          {stringValue}
        </div>
      );
    }

    return (
      <div className="text-sm" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
        {stringValue}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Main Content */}
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Only show back button for agent-specific reports, not centralized reports */}
              {agentId !== "reports" && (
                <button
                  onClick={() => navigate(`/agents/${agentId}`)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="text-gray-600 dark:text-gray-400" />
                </button>
              )}
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {definition.metadata?.displayName || 'Report'}
              </h1>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <FontAwesomeIcon icon={faFilter} className="text-gray-600 dark:text-gray-400" />
              </button>
            </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={loadReportData}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={loading ? faSpinner : faSync} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <div className="relative group">
                  <button className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                    <FontAwesomeIcon icon={faDownload} className="mr-2" />
                    Export
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <button
                      onClick={() => exportData('csv')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg"
                    >
                      <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                      Export as CSV
                    </button>
                    <button
                      onClick={() => exportData('json')}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg"
                    >
                      <FontAwesomeIcon icon={faFileCode} className="mr-2" />
                      Export as JSON
                    </button>
                  </div>
                </div>
              </div>
            </div>

          {/* Summary Stats */}
          <div className="mt-4 flex items-center space-x-6 text-sm text-gray-600 dark:text-gray-400">
            <span>Total Records: {data.length}</span>
            <span>Filtered: {filteredData.length}</span>
            <span>Page {currentPage} of {totalPages || 1}</span>
          </div>
        </div>

        {/* Filters Section - Collapsible */}
        {showFilters && (
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex flex-wrap items-end gap-4">
              {/* Global Search */}
              <div className="relative min-w-64">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Search all fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 pl-9 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 bottom-3 text-gray-400" />
              </div>

              {/* Dynamic Filters from Definition */}
              {definition.filters?.map(filter => (
                <div key={filter.name} className="min-w-48">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {filter.label}
                  </label>
                  {filter.type === 'select' && (
                    <select
                      value={filters[filter.name] || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, [filter.name]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">All</option>
                      {filter.options?.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                  {filter.type === 'text' && (
                    <input
                      type="text"
                      value={filters[filter.name] || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, [filter.name]: e.target.value }))}
                      placeholder={filter.placeholder}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  )}
                </div>
              ))}

              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setFilters({});
                    setSearchTerm('');
                  }}
                  className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <FontAwesomeIcon icon={faSpinner} className="text-4xl text-gray-400 animate-spin" />
              </div>
            ) : currentData.length > 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      {columns.map(column => (
                        <th
                          key={column.key}
                          onClick={() => column.sortable !== false && handleSort(column.key)}
                          className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider ${
                            column.sortable !== false ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600' : ''
                          }`}
                        >
                          <div className="flex items-center space-x-1">
                            <span>{column.label}</span>
                            {sortConfig.key === column.key && (
                              <span className="text-blue-500">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {currentData.map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        {columns.map(column => (
                          <td 
                            key={column.key} 
                            className="px-6 py-4 text-gray-900 dark:text-white align-top"
                            style={{ maxWidth: column.width || '300px' }}
                          >
                            {renderCellContent(row[column.key], column)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                <FontAwesomeIcon icon={faTable} className="text-4xl mb-4" />
                <p>No data available</p>
              </div>
            )}
          </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Items per page:
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportRenderer;